import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Separator } from '../../components/ui/separator';
import {
  ArrowLeft, Loader2, Search, Plus, Users, Shield, Wrench, User,
  Mail, Phone, Key, Check, X, Eye, EyeOff, ToggleLeft, ToggleRight,
  Trash2, AlertTriangle, Sparkles, Archive, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import UserAvatar from '../../components/UserAvatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Regex that catches the standard test-pollution patterns (TEST_, UITEST_, Test_, @example.com etc.)
// Mirrors the backend cleanup script so the "Select test users" button matches the same patterns.
// IMPORTANT: @gleamgurus.internal is intentionally EXCLUDED from this regex. Those are real
// concierge-managed customers Quan added without an email — they get a separate "Concierge" pill.
const TEST_PATTERN_NAME = /^(TEST_|UITEST_|Test_|TEST |UITEST |Test Carpet|Test Booking|Test Stain|Test Laundry|Test Stripe|TestUser|Stripe Test|Jane Test|Account Tester|Phone Client|Client Test|Test User|TEST UI|Updated Client|Test Client|Card Capture Test|Flow Test|Resend Test|Sales Test|Concierge Test|Demo Customer|Sweep Pytest|Now Pytest|Schedule Pytest|Pytest)/i;
const TEST_PATTERN_EMAIL = /(^test_|^test\+|^test@|^uitest_|^plain\+|^regtest\+|^e2e_test@|^pytest-|^iter\d+-|^acct-test-|^cc-test-|^cc-flow-|^sales-test@|^resend-test@|^demo-customer@|^sweep-[0-9a-f]+@|^now-[0-9a-f]+@|^sched-[0-9a-f]+@|^referee\+[0-9]+@|@(test|fake|tester|dummy|noemail|acme-test|example)\.)/i;

const isTestUser = (u) =>
  u.role !== 'master_admin' && (
    TEST_PATTERN_NAME.test(u.full_name || '') ||
    TEST_PATTERN_EMAIL.test(u.email || '')
  );

// Concierge-managed = real walk-in customer Quan added via the CRM without an email.
// The placeholder email `concierge+xxx@gleamgurus.internal` is auto-generated by the backend
// (routes/crm.py create_manual_client) to satisfy the unique-email index. These are REAL
// customers, just no email on file — NOT test data — but Master Admin can still bulk-purge them.
const isConciergeManaged = (u) =>
  u.concierge_managed === true
  || u.email_is_placeholder === true
  || /@gleamgurus\.internal$/i.test(u.email || '');

const displayEmail = (u) => (isConciergeManaged(u) ? '(no email on file)' : (u.email || '—'));

const ROLE_CONFIG = {
  master_admin: { label: 'Master Admin', color: 'bg-gradient-to-r from-amber-100 to-orange-100 text-orange-700 border-orange-300', icon: Shield },
  admin:   { label: 'Admin',   color: 'bg-purple-50 text-purple-700 border-purple-200', icon: Shield },
  cleaner: { label: 'Cleaner', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: Wrench },
  client:  { label: 'Client',  color: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: User },
};

const ROLES = ['all', 'master_admin', 'admin', 'cleaner', 'client'];

const AdminUsersPage = () => {
  const { token, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive | archived
  const [showArchived, setShowArchived] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  // Multi-select for bulk hard-delete (master_admin only)
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [confirmText, setConfirmText] = useState('');
  const [purgeDialogOpen, setPurgeDialogOpen] = useState(false);
  const [purging, setPurging] = useState(false);

  const isMasterAdmin = currentUser?.role === 'master_admin';

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchUsers = useCallback(async () => {
    try {
      const params = {};
      if (roleFilter !== 'all') params.role = roleFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (showArchived || statusFilter === 'archived') params.show_archived = true;
      if (search) params.search = search;
      const res = await axios.get(`${API_URL}/api/admin-users`, { headers, params });
      setUsers(res.data);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, [headers, roleFilter, search, statusFilter, showArchived]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const { admins, cleaners, clients } = useMemo(() => ({
    admins: users.filter(u => u.role === 'admin').length,
    cleaners: users.filter(u => u.role === 'cleaner').length,
    clients: users.filter(u => u.role === 'client').length,
  }), [users]);

  const toggleActive = async (user) => {
    setActionLoading(`toggle-${user.id}`);
    try {
      await axios.patch(`${API_URL}/api/admin-users/${user.id}`, { is_active: !user.is_active }, { headers });
      toast.success(`${user.full_name} ${user.is_active ? 'deactivated' : 'activated'}`);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setActionLoading(''); }
  };

  const changeStatus = async (user, newStatus) => {
    setActionLoading(`status-${user.id}`);
    try {
      await axios.post(`${API_URL}/api/admin-users/${user.id}/status`, { status: newStatus }, { headers });
      const verbs = { active: 'restored', inactive: 'deactivated', archived: 'archived' };
      toast.success(`${user.full_name} ${verbs[newStatus] || 'updated'}`);
      fetchUsers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update status'); }
    finally { setActionLoading(''); }
  };

  const [singleDelete, setSingleDelete] = useState({ open: false, user: null, safety: null, loading: false, confirmText: '' });

  const openSingleDelete = async (user) => {
    setSingleDelete({ open: true, user, safety: null, loading: true, confirmText: '' });
    try {
      const res = await axios.get(`${API_URL}/api/admin-users/${user.id}/delete-safety`, { headers });
      setSingleDelete(prev => ({ ...prev, safety: res.data, loading: false }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not check delete safety');
      setSingleDelete({ open: false, user: null, safety: null, loading: false, confirmText: '' });
    }
  };

  const confirmSingleDelete = async ({ force = false } = {}) => {
    if (singleDelete.confirmText !== 'DELETE') {
      toast.error('Type DELETE exactly to confirm');
      return;
    }
    setSingleDelete(prev => ({ ...prev, loading: true }));
    try {
      const url = `${API_URL}/api/admin-users/${singleDelete.user.id}/hard${force ? '?force=true' : ''}`;
      await axios.delete(url, { headers });
      toast.success(`${singleDelete.user.full_name} permanently deleted${force ? ' (forced — business records overridden)' : ''}`);
      setSingleDelete({ open: false, user: null, safety: null, loading: false, confirmText: '', forcing: false });
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || 'Delete failed');
      setSingleDelete(prev => ({ ...prev, loading: false }));
    }
  };

  const toggleSelected = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const selectAllTestUsers = () => {
    const testIds = users.filter(isTestUser).map(u => u.id);
    setSelectedIds(new Set(testIds));
    toast.success(`Selected ${testIds.length} test pattern${testIds.length === 1 ? '' : 's'}`);
  };

  const selectAllConcierge = () => {
    const ids = users
      .filter(u => u.role !== 'master_admin' && u.id !== currentUser?.id && isConciergeManaged(u))
      .map(u => u.id);
    setSelectedIds(new Set(ids));
    toast.success(`Selected ${ids.length} concierge placeholder${ids.length === 1 ? '' : 's'} (no email on file)`);
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleBulkPurge = async () => {
    if (confirmText !== 'DELETE TEST USERS') {
      toast.error('Type DELETE TEST USERS exactly to confirm');
      return;
    }
    setPurging(true);
    try {
      const res = await axios.post(`${API_URL}/api/admin-users/bulk-hard-delete`,
        { user_ids: Array.from(selectedIds) }, { headers });
      const { deleted_count, skipped } = res.data;
      toast.success(`Permanently deleted ${deleted_count} user${deleted_count === 1 ? '' : 's'} and their related records`);
      if (skipped?.length) {
        toast.warning(`${skipped.length} user${skipped.length === 1 ? ' was' : 's were'} skipped (master admins, yourself, or users with real business records)`);
      }
      clearSelection();
      setConfirmText('');
      setPurgeDialogOpen(false);
      fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk delete failed');
    } finally { setPurging(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50">
      <Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" />
    </div>
  );

  if (showCreate) return (
    <CreateUserForm headers={headers} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchUsers(); }} />
  );

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="admin-users-page">
      {/* Header */}
      <div className="gg-legacy-head bg-white border-b border-zinc-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-zinc-900">User Management</h1>
              <p className="text-xs text-zinc-500">{users.length} total users</p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-[#F56F05] hover:bg-[#e06505] text-white rounded-xl" data-testid="create-user-btn">
            <Plus className="h-4 w-4 mr-2" /> Create User
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[
            { label: 'Admins', count: admins, color: 'text-purple-600', bg: 'bg-purple-50', icon: Shield },
            { label: 'Cleaners', count: cleaners, color: 'text-blue-600', bg: 'bg-blue-50', icon: Wrench },
            { label: 'Clients', count: clients, color: 'text-emerald-600', bg: 'bg-emerald-50', icon: Users },
          ].map(stat => (
            <Card key={stat.label} className="border-zinc-100">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl ${stat.bg} flex items-center justify-center`}>
                  <stat.icon className={`h-5 w-5 ${stat.color}`} />
                </div>
                <div>
                  <p className="text-xs text-zinc-500">{stat.label}</p>
                  <p className={`text-lg font-bold ${stat.color}`}>{stat.count}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input placeholder="Search by name, email, or phone..."
              value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 rounded-xl border-zinc-200" data-testid="search-input" />
          </div>
          <div className="flex gap-2 flex-wrap">
            {ROLES.map(r => (
              <Button key={r} variant={roleFilter === r ? 'default' : 'outline'} size="sm"
                className={`rounded-full text-xs capitalize ${roleFilter === r ? 'bg-zinc-900 text-white' : 'border-zinc-200'}`}
                onClick={() => setRoleFilter(r)} data-testid={`filter-${r}`}>
                {r === 'all' ? 'All' : ROLE_CONFIG[r]?.label || r}
              </Button>
            ))}
          </div>
        </div>

        {/* Status filter chips */}
        <div className="flex flex-wrap items-center gap-2" data-testid="status-filter-row">
          <span className="text-xs text-zinc-400 font-semibold uppercase tracking-wider mr-1">Status:</span>
          {['all', 'active', 'inactive', 'archived'].map(s => (
            <Button
              key={s}
              variant={statusFilter === s ? 'default' : 'outline'}
              size="sm"
              className={`rounded-full text-xs capitalize ${statusFilter === s ? 'bg-[#F56F05] hover:bg-[#e06505] text-white border-transparent' : 'border-zinc-200'}`}
              onClick={() => setStatusFilter(s)}
              data-testid={`status-filter-${s}`}
            >
              {s === 'all' ? 'All' : s}
            </Button>
          ))}
          {statusFilter !== 'archived' && (
            <label className="flex items-center gap-1.5 text-xs text-zinc-500 ml-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={e => setShowArchived(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-zinc-300"
                data-testid="show-archived-checkbox"
              />
              Show archived
            </label>
          )}
        </div>

        {/* Bulk action bar — only visible to master_admin */}
        {isMasterAdmin && (
          <div className="flex flex-wrap items-center gap-3 bg-white border border-zinc-100 rounded-xl px-4 py-2.5" data-testid="bulk-action-bar">
            <div className="text-sm text-zinc-600">
              <span className="font-semibold text-zinc-900">{selectedIds.size}</span> selected
              {users.filter(isTestUser).length > 0 && (
                <span className="text-zinc-400 ml-2">
                  · {users.filter(isTestUser).length} test pattern{users.filter(isTestUser).length === 1 ? '' : 's'} detected
                </span>
              )}
              {users.filter(u => u.role !== 'master_admin' && isConciergeManaged(u)).length > 0 && (
                <span className="text-zinc-400 ml-2">
                  · {users.filter(u => u.role !== 'master_admin' && isConciergeManaged(u)).length} concierge placeholder{users.filter(u => u.role !== 'master_admin' && isConciergeManaged(u)).length === 1 ? '' : 's'}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2 ml-auto">
              <Button size="sm" variant="outline" onClick={selectAllTestUsers} className="text-xs rounded-full" data-testid="select-test-users-btn">
                <Sparkles className="h-3 w-3 mr-1.5" /> Select test users
              </Button>
              <Button size="sm" variant="outline" onClick={selectAllConcierge} className="text-xs rounded-full" data-testid="select-concierge-users-btn">
                <Sparkles className="h-3 w-3 mr-1.5" /> Select concierge (no email)
              </Button>
              {selectedIds.size > 0 && (
                <>
                  <Button size="sm" variant="ghost" onClick={clearSelection} className="text-xs rounded-full" data-testid="clear-selection-btn">
                    Clear
                  </Button>
                  <Button size="sm" onClick={() => setPurgeDialogOpen(true)} className="bg-red-600 hover:bg-red-700 text-white text-xs rounded-full" data-testid="bulk-delete-btn">
                    <Trash2 className="h-3 w-3 mr-1.5" /> Delete selected ({selectedIds.size})
                  </Button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Users Table */}
        <Card className="border-zinc-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead>
                <tr className="border-b border-zinc-100 bg-zinc-50/50">
                  {isMasterAdmin && (
                    <th className="text-left text-xs font-semibold text-zinc-500 px-3 py-3 w-10">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-zinc-300 cursor-pointer"
                        checked={users.length > 0 && users.every(u => selectedIds.has(u.id) || u.role === 'master_admin' || u.id === currentUser?.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const all = users.filter(u => u.role !== 'master_admin' && u.id !== currentUser?.id).map(u => u.id);
                            setSelectedIds(new Set(all));
                          } else {
                            clearSelection();
                          }
                        }}
                        data-testid="select-all-checkbox"
                        title="Select all (excludes master admins + yourself)"
                      />
                    </th>
                  )}
                  <th className="text-left text-xs font-semibold text-zinc-500 px-4 py-3">User</th>
                  <th className="text-left text-xs font-semibold text-zinc-500 px-4 py-3 hidden md:table-cell">Contact</th>
                  <th className="text-center text-xs font-semibold text-zinc-500 px-4 py-3">Role</th>
                  <th className="text-center text-xs font-semibold text-zinc-500 px-4 py-3">Status</th>
                  <th className="text-right text-xs font-semibold text-zinc-500 px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => {
                  const cfg = ROLE_CONFIG[user.role] || ROLE_CONFIG.client;
                  const canSelect = user.role !== 'master_admin' && user.id !== currentUser?.id;
                  const isSelected = selectedIds.has(user.id);
                  const looksLikeTest = isTestUser(user);
                  const isConcierge = isConciergeManaged(user);
                  return (
                    <tr
                      key={user.id}
                      className={`border-b border-zinc-50 hover:bg-zinc-50/50 transition-colors ${
                        isSelected ? 'bg-orange-50/40' : ''
                      }`}
                      data-testid={`user-row-${user.id}`}
                    >
                      {isMasterAdmin && (
                        <td className="px-3 py-3">
                          {canSelect ? (
                            <input
                              type="checkbox"
                              className="h-4 w-4 rounded border-zinc-300 cursor-pointer"
                              checked={isSelected}
                              onChange={() => toggleSelected(user.id)}
                              data-testid={`select-${user.id}`}
                            />
                          ) : (
                            <Shield className="h-3.5 w-3.5 text-zinc-300" title="Cannot be bulk-deleted" />
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <UserAvatar user={user} size="sm" testId={`user-avatar-${user.id}`} />
                          <div className="min-w-0">
                            <button
                              type="button"
                              onClick={() => navigate(`/admin/users/${user.id}`)}
                              className="font-semibold text-sm text-zinc-900 truncate flex items-center gap-1.5 hover:text-[#F56F05] transition-colors text-left"
                              data-testid={`view-name-${user.id}`}
                            >
                              {user.full_name}
                              {looksLikeTest && (
                                <span className="text-[9px] font-bold text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded-full uppercase tracking-wider" title="Matches a known test-pattern name/email" data-testid={`test-badge-${user.id}`}>
                                  test
                                </span>
                              )}
                              {isConcierge && !looksLikeTest && (
                                <span className="text-[9px] font-bold text-zinc-600 bg-zinc-100 px-1.5 py-0.5 rounded-full uppercase tracking-wider" title="Concierge-managed customer (no email on file). Created by an admin without providing an email." data-testid={`concierge-badge-${user.id}`}>
                                  concierge
                                </span>
                              )}
                            </button>
                            <p className="text-[11px] text-zinc-400 md:hidden">{displayEmail(user)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <div className="flex items-center gap-1 text-xs text-zinc-500"><Mail className="h-3 w-3" /> {displayEmail(user)}</div>
                        {user.phone && <div className="flex items-center gap-1 text-xs text-zinc-400 mt-0.5"><Phone className="h-3 w-3" /> {user.phone}</div>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge className={`${cfg.color} border text-[10px] font-semibold`}>{cfg.label}</Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {(() => {
                          const s = user.status || (user.is_active !== false ? 'active' : 'inactive');
                          const cls = s === 'active'
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : s === 'archived'
                              ? 'bg-zinc-100 text-zinc-500 border-zinc-200'
                              : 'bg-red-50 text-red-600 border-red-200';
                          return <Badge className={`border text-[10px] font-semibold capitalize ${cls}`} data-testid={`status-${user.id}`}>{s}</Badge>;
                        })()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1 flex-wrap">
                          <Button variant="ghost" size="sm" className="h-8 text-xs text-zinc-500 hover:text-[#F56F05]"
                            onClick={() => navigate(`/admin/users/${user.id}`)} data-testid={`view-${user.id}`}>
                            View
                          </Button>
                          <Button variant="ghost" size="sm" className="h-8 text-xs text-zinc-500"
                            onClick={() => { setEditUser(user); setEditOpen(true); }} data-testid={`edit-${user.id}`}>
                            Edit
                          </Button>
                          {isMasterAdmin && user.role !== 'master_admin' && user.id !== currentUser?.id && (() => {
                            const s = user.status || (user.is_active !== false ? 'active' : 'inactive');
                            if (s === 'archived') {
                              return (
                                <Button variant="ghost" size="sm" className="h-8 text-xs text-emerald-600"
                                  onClick={() => changeStatus(user, 'active')}
                                  disabled={actionLoading === `status-${user.id}`}
                                  data-testid={`restore-${user.id}`}>
                                  <RotateCcw className="h-3 w-3 mr-1" /> Restore
                                </Button>
                              );
                            }
                            return (
                              <>
                                <Button variant="ghost" size="sm" className="h-8 text-xs text-zinc-500"
                                  onClick={() => changeStatus(user, s === 'active' ? 'inactive' : 'active')}
                                  disabled={actionLoading === `status-${user.id}`}
                                  title={s === 'active' ? 'Deactivate (login blocked)' : 'Reactivate'}
                                  data-testid={`deactivate-${user.id}`}>
                                  {s === 'active' ? 'Deactivate' : 'Reactivate'}
                                </Button>
                                <Button variant="ghost" size="sm" className="h-8 text-xs text-zinc-500 hover:text-amber-700"
                                  onClick={() => changeStatus(user, 'archived')}
                                  disabled={actionLoading === `status-${user.id}`}
                                  title="Archive (hide from lists, keep data)"
                                  data-testid={`archive-${user.id}`}>
                                  <Archive className="h-3 w-3 mr-1" /> Archive
                                </Button>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:bg-red-50"
                                  onClick={() => openSingleDelete(user)}
                                  title="Delete (only if no business records)"
                                  data-testid={`delete-${user.id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            );
                          })()}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* Edit User Modal */}
      <EditUserModal
        key={editUser?.id || 'none'}
        user={editUser}
        open={editOpen}
        onClose={() => setEditOpen(false)}
        headers={headers}
        onUpdated={() => { setEditOpen(false); fetchUsers(); }}
        isMasterAdmin={isMasterAdmin}
        currentUserId={currentUser?.id}
        onRequestDelete={(u) => { setEditOpen(false); openSingleDelete(u); }}
      />

      {/* Single-user safe-delete dialog */}
      <Dialog open={singleDelete.open} onOpenChange={(o) => { if (!o) setSingleDelete({ open: false, user: null, safety: null, loading: false, confirmText: '' }); }}>
        <DialogContent className="sm:max-w-md" data-testid="single-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete {singleDelete.user?.full_name}?
            </DialogTitle>
          </DialogHeader>
          {singleDelete.loading && !singleDelete.safety ? (
            <div className="py-6 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#F56F05]" /><p className="text-sm text-zinc-500 mt-2">Checking safety…</p></div>
          ) : singleDelete.safety && !singleDelete.safety.can_hard_delete ? (
            singleDelete.forcing ? (
              /* Master-admin force-delete override: show extra warning + DELETE typing */
              <div className="space-y-3 py-2" data-testid="force-delete-confirm">
                <div className="bg-red-50 border-2 border-red-300 rounded-xl p-3">
                  <p className="text-sm font-bold text-red-700">⚠️ Force delete — business records will be wiped</p>
                  <p className="text-xs text-red-600 mt-1">
                    You are overriding safety checks. The following records will also be permanently deleted along with the user:
                  </p>
                  <ul className="text-xs text-red-700 mt-2 space-y-0.5 ml-3 list-disc">
                    {singleDelete.safety.blockers.map(b => (
                      <li key={b}>{b.replace(/_/g, ' ')}: <strong>{singleDelete.safety.counts[b]}</strong></li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-red-500 mt-2">This action will be flagged in the audit log as a forced delete.</p>
                </div>
                <div>
                  <Label className="text-xs text-zinc-500">Type <code className="bg-zinc-100 px-1.5 py-0.5 rounded text-red-600 font-bold">DELETE</code> to confirm force delete</Label>
                  <Input
                    value={singleDelete.confirmText}
                    onChange={e => setSingleDelete(prev => ({ ...prev, confirmText: e.target.value }))}
                    placeholder="DELETE"
                    className="mt-1 rounded-xl border-red-300 font-mono"
                    data-testid="force-delete-confirm-input"
                    autoFocus
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setSingleDelete(prev => ({ ...prev, forcing: false, confirmText: '' }))}
                  >
                    Back
                  </Button>
                  <Button
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                    onClick={() => confirmSingleDelete({ force: true })}
                    disabled={singleDelete.loading || singleDelete.confirmText !== 'DELETE'}
                    data-testid="force-delete-confirm-btn"
                  >
                    {singleDelete.loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                    Force delete
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3 py-2" data-testid="cannot-delete-message">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                  <p className="text-sm font-semibold text-amber-900">
                    This profile is connected to business records and cannot be permanently deleted without override.
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    Recommended: archive or deactivate this profile. Master admins can also force-delete for pre-launch test cleanup.
                  </p>
                </div>
                <div className="text-xs text-zinc-500 space-y-1">
                  <p className="font-semibold uppercase tracking-wider text-[10px]">Blockers:</p>
                  {singleDelete.safety.blockers.map(b => (
                    <p key={b}>• {b.replace(/_/g, ' ')}: <span className="font-semibold text-zinc-700">{singleDelete.safety.counts[b]}</span></p>
                  ))}
                </div>
                <div className="flex gap-2 pt-1">
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => { changeStatus(singleDelete.user, 'inactive'); setSingleDelete({ open: false, user: null, safety: null, loading: false, confirmText: '', forcing: false }); }}
                    data-testid="fallback-deactivate"
                  >
                    Deactivate
                  </Button>
                  <Button
                    className="flex-1 bg-[#F56F05] hover:bg-[#e06505] text-white"
                    onClick={() => { changeStatus(singleDelete.user, 'archived'); setSingleDelete({ open: false, user: null, safety: null, loading: false, confirmText: '', forcing: false }); }}
                    data-testid="fallback-archive"
                  >
                    <Archive className="h-4 w-4 mr-2" /> Archive
                  </Button>
                </div>
                {isMasterAdmin && (
                  <Button
                    variant="outline"
                    className="w-full border-red-300 text-red-600 hover:bg-red-50"
                    onClick={() => setSingleDelete(prev => ({ ...prev, forcing: true, confirmText: '' }))}
                    data-testid="force-delete-init-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-2" /> Force delete (override blockers)
                  </Button>
                )}
              </div>
            )
          ) : singleDelete.safety ? (
            <div className="space-y-3 py-2">
              <p className="text-sm text-zinc-600">
                This will permanently delete <span className="font-semibold">{singleDelete.user?.full_name}</span> and their profile data.
                No connected business records were found.
              </p>
              <div>
                <Label className="text-xs text-zinc-500">Type <code className="bg-zinc-100 px-1.5 py-0.5 rounded text-red-600 font-bold">DELETE</code> to confirm</Label>
                <Input
                  value={singleDelete.confirmText}
                  onChange={e => setSingleDelete(prev => ({ ...prev, confirmText: e.target.value }))}
                  placeholder="DELETE"
                  className="mt-1 rounded-xl border-zinc-200 font-mono"
                  data-testid="single-delete-confirm-input"
                  autoFocus
                />
              </div>
              <div className="flex gap-2 pt-1">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setSingleDelete({ open: false, user: null, safety: null, loading: false, confirmText: '' })}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  onClick={confirmSingleDelete}
                  disabled={singleDelete.loading || singleDelete.confirmText !== 'DELETE'}
                  data-testid="single-delete-confirm-btn"
                >
                  {singleDelete.loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Permanently delete
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Bulk hard-delete confirmation */}
      <Dialog open={purgeDialogOpen} onOpenChange={(o) => { if (!o) { setPurgeDialogOpen(false); setConfirmText(''); } }}>
        <DialogContent className="sm:max-w-md" data-testid="purge-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" /> Permanently delete {selectedIds.size} user{selectedIds.size === 1 ? '' : 's'}?
            </DialogTitle>
            <DialogDescription>
              You are about to permanently delete selected test/demo users and their fake related records.
              This removes the user accounts plus all their related rows: addresses, appointments,
              time entries, QC items, invoices (by email), card-capture links, payouts, referrals, etc.
              <span className="font-semibold"> This cannot be undone.</span>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-zinc-600">
              <span className="font-semibold">Always skipped:</span> master admins, your own account, and users with real business records (paid invoices, completed jobs, time entries, Stripe linkage).
            </p>
            <div>
              <Label className="text-xs text-zinc-500">Type <code className="bg-zinc-100 px-1.5 py-0.5 rounded text-red-600 font-bold">DELETE TEST USERS</code> to confirm</Label>
              <Input
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder="DELETE TEST USERS"
                className="mt-1 rounded-xl border-zinc-200 font-mono"
                data-testid="purge-confirm-input"
                autoFocus
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => { setPurgeDialogOpen(false); setConfirmText(''); }}>
                Cancel
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                onClick={handleBulkPurge}
                disabled={purging || confirmText !== 'DELETE TEST USERS'}
                data-testid="confirm-purge-btn"
              >
                {purging ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Permanently delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ EDIT USER MODAL ============

const EditUserModal = ({ user, open, onClose, headers, onUpdated, isMasterAdmin = false, currentUserId, onRequestDelete }) => {
  // Derive initial values lazily from the user prop. The dialog mounts with key={user?.id}
  // so a fresh user causes remount + lazy init — no useEffect→setState sync needed.
  const initialStatus = user?.status || (user?.is_active !== false ? 'active' : 'inactive');
  const [name, setName] = useState(user?.full_name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [role, setRole] = useState(user?.role || 'client');
  const [status, setStatus] = useState(initialStatus);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [alsoCleaner, setAlsoCleaner] = useState(!!user?.also_cleaner);
  const [togglingCleaner, setTogglingCleaner] = useState(false);

  if (!user) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = {};
      if (name !== user.full_name) updates.full_name = name;
      if (phone !== (user.phone || '')) updates.phone = phone;
      if (role !== user.role) updates.role = role;
      const currentStatus = user.status || (user.is_active !== false ? 'active' : 'inactive');
      if (status !== currentStatus) updates.status = status;
      if (Object.keys(updates).length === 0) { toast.info('No changes to save'); setSaving(false); return; }
      await axios.patch(`${API_URL}/api/admin-users/${user.id}`, updates, { headers });
      toast.success('User updated');
      onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
    finally { setSaving(false); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setResetting(true);
    try {
      await axios.post(`${API_URL}/api/admin-users/${user.id}/reset-password`, { new_password: newPassword }, { headers });
      toast.success('Password reset successfully');
      setNewPassword('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reset password'); }
    finally { setResetting(false); }
  };

  const toggleCleanerRole = async () => {
    const next = !alsoCleaner;
    setTogglingCleaner(true);
    try {
      // Default to "unpaid mode" (hourly_rate=0) so dual-role admins can be assigned to jobs
      // without being included in payout calculations. They can switch to paid mode later
      // by editing their cleaner profile on the Cleaners page.
      await axios.post(
        `${API_URL}/api/admin-users/${user.id}/cleaner-role`,
        { enabled: next, hourly_rate: 0 },
        { headers }
      );
      setAlsoCleaner(next);
      toast.success(next
        ? `${user.full_name} can now be assigned to jobs (unpaid — no payout yet)`
        : `Cleaner role removed for ${user.full_name}`
      );
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update cleaner role');
    } finally { setTogglingCleaner(false); }
  };

  const cfg = ROLE_CONFIG[user.role] || ROLE_CONFIG.client;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="edit-user-modal">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>{user.email}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Current Info */}
          <div className="flex items-center gap-2">
            <Badge className={`${cfg.color} border text-xs`}>{cfg.label}</Badge>
            <Badge className={`border text-xs ${user.is_active !== false ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
              {user.is_active !== false ? 'Active' : 'Inactive'}
            </Badge>
            <span className="text-[10px] text-zinc-400 ml-auto">Joined {new Date(user.created_at).toLocaleDateString()}</span>
          </div>

          <Separator />

          {/* Edit Fields */}
          <div>
            <Label className="text-xs text-zinc-500">Full Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} className="mt-1 rounded-xl border-zinc-200" data-testid="edit-name" />
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Phone</Label>
            <Input value={phone} onChange={e => setPhone(e.target.value)} className="mt-1 rounded-xl border-zinc-200" placeholder="(971) 555-1234" data-testid="edit-phone" />
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Role</Label>
            {(() => {
              const isSelf = user.id === currentUserId;
              const targetIsMaster = user.role === 'master_admin';
              // Rules 1-3 + 6: Role dropdown is LOCKED when editing yourself
              // OR when the target is a master_admin and the actor isn't. Server
              // enforces the same rules — this is just to prevent accidental UX.
              const roleLocked = isSelf || (targetIsMaster && !isMasterAdmin);
              return (
                <>
                  <Select value={role} onValueChange={setRole} disabled={roleLocked}>
                    <SelectTrigger className="mt-1 rounded-xl border-zinc-200" data-testid="edit-role">
                      <SelectValue placeholder={targetIsMaster ? 'Master Admin' : undefined}>
                        {role === 'master_admin' ? 'Master Admin' : (role ? role.charAt(0).toUpperCase() + role.slice(1) : undefined)}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {/* Only master admins can grant/hold the master_admin role.
                          The item is shown for context even when locked. */}
                      {(targetIsMaster || isMasterAdmin) && (
                        <SelectItem value="master_admin" disabled={!isMasterAdmin || isSelf}>Master Admin</SelectItem>
                      )}
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="cleaner">Cleaner</SelectItem>
                      <SelectItem value="client">Client</SelectItem>
                    </SelectContent>
                  </Select>
                  {roleLocked && (
                    <p className="mt-1.5 text-[11px] text-zinc-500 leading-snug" data-testid="edit-role-locked-note">
                      {isSelf
                        ? 'Your Master Admin role cannot be changed from your own account.'
                        : 'Only another Master Admin can change a Master Admin\u2019s role.'}
                    </p>
                  )}
                </>
              );
            })()}
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Status</Label>
            {(() => {
              const isSelf = user.id === currentUserId;
              // Rule 5: block self-status change so a master admin can't
              // accidentally lock themselves out of the system.
              return (
                <>
                  <Select value={status} onValueChange={setStatus} disabled={isSelf}>
                    <SelectTrigger className="mt-1 rounded-xl border-zinc-200" data-testid="edit-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active — can log in normally</SelectItem>
                      <SelectItem value="inactive">Inactive — login blocked, data preserved</SelectItem>
                      <SelectItem value="archived">Archived — hidden from lists, recoverable</SelectItem>
                    </SelectContent>
                  </Select>
                  {isSelf && (
                    <p className="mt-1.5 text-[11px] text-zinc-500 leading-snug" data-testid="edit-status-locked-note">
                      You can&apos;t change your own account status.
                    </p>
                  )}
                </>
              );
            })()}
          </div>

          <Button onClick={handleSave} disabled={saving} className="w-full bg-[#F56F05] hover:bg-[#e06505] text-white rounded-xl" data-testid="save-user-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            Save Changes
          </Button>

          <Separator />

          {/* Reset Password */}
          <div>
            <p className="text-xs font-semibold text-zinc-500 mb-2 flex items-center gap-1"><Key className="h-3 w-3" /> Reset Password</p>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input type={showPassword ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  className="rounded-xl border-zinc-200 pr-9" placeholder="New password (min 6 chars)" data-testid="new-password" />
                <button onClick={() => setShowPassword(!showPassword)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <Button variant="outline" onClick={handleResetPassword} disabled={resetting || !newPassword} className="rounded-xl" data-testid="reset-password-btn">
                {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Reset'}
              </Button>
            </div>
          </div>

          {/* Dual-role toggle — only for admin and master_admin users */}
          {(user.role === 'admin' || user.role === 'master_admin') && (
            <div className="rounded-xl border border-zinc-200 bg-zinc-50/60 p-3" data-testid="dual-role-toggle">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-zinc-800">Also acts as a cleaner</p>
                  <p className="text-[11px] text-zinc-500 leading-snug mt-0.5">
                    When on, this admin can be assigned to appointments as the cleaner.
                    <span className="block mt-0.5 text-zinc-400">Defaults to <strong>unpaid mode</strong> ($0/hr, excluded from payouts). Set an hourly rate on the Cleaners page if you want to pay them.</span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={toggleCleanerRole}
                  disabled={togglingCleaner}
                  role="switch"
                  aria-checked={alsoCleaner}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#F56F05] focus:ring-offset-2 ${alsoCleaner ? 'bg-[#F56F05]' : 'bg-zinc-300'} ${togglingCleaner ? 'opacity-60' : ''}`}
                  data-testid="dual-role-switch"
                >
                  <span
                    aria-hidden="true"
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${alsoCleaner ? 'translate-x-5' : 'translate-x-0'}`}
                  />
                </button>
              </div>
            </div>
          )}

          {/* Danger Zone — master admin only; hidden for self and other master admins */}
          {isMasterAdmin && user.id !== currentUserId && user.role !== 'master_admin' && onRequestDelete && (
            <div
              className="mt-4 rounded-xl border-2 border-red-200 bg-red-50/40 p-4 space-y-2"
              data-testid="edit-user-danger-zone"
            >
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-red-600" />
                <p className="text-sm font-bold text-red-700">Danger Zone</p>
              </div>
              <p className="text-xs text-red-600/90 leading-snug">
                Permanently removes this user and all related profile data, addresses, and uncompleted appointments.
                If the user has business records (invoices, time entries, payouts) you&apos;ll be given a force-delete option for pre-launch cleanup.
                This cannot be undone and is recorded in the audit log.
              </p>
              <Button
                variant="outline"
                className="w-full border-red-300 text-red-700 hover:bg-red-100 hover:text-red-800 mt-2"
                onClick={() => onRequestDelete(user)}
                data-testid="edit-user-delete-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" /> Permanently Delete User
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ CREATE USER FORM ============

const CreateUserForm = ({ headers, onClose, onCreated }) => {
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [sendInvite, setSendInvite] = useState(true); // default: invite-email flow (no password typed by admin)
  const [form, setForm] = useState({
    full_name: '', email: '', phone: '', password: '', role: 'client',
  });

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const canSubmit = sendInvite
    ? !!(form.full_name && form.email)
    : !!(form.full_name && form.email && form.password && form.password.length >= 6);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = sendInvite
        ? { ...form, password: undefined, send_invite: true }
        : { ...form, send_invite: false };
      await axios.post(`${API_URL}/api/admin-users`, payload, { headers });
      toast.success(
        sendInvite
          ? `Invite sent to ${form.email}`
          : `${form.full_name} created as ${form.role}`
      );
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create user');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="create-user-form">
      {/* Header */}
      <div className="bg-white border-b border-zinc-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-create-form">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-xl font-bold text-zinc-900">Create User</h1>
          </div>
          <Button onClick={handleSubmit} disabled={!canSubmit || submitting}
            className="bg-[#F56F05] hover:bg-[#e06505] text-white rounded-xl" data-testid="submit-user-btn">
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            Create Account
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Role Selection */}
        <Card className="border-zinc-100">
          <CardContent className="p-4 space-y-3">
            <p className="text-sm font-semibold text-zinc-700">Account Type</p>
            <div className="grid grid-cols-3 gap-3">
              {(['client', 'cleaner', 'admin']).map(r => {
                const cfg = ROLE_CONFIG[r];
                const IconComp = cfg.icon;
                return (
                  <button key={r} onClick={() => set('role', r)}
                    className={`p-4 rounded-xl border-2 text-center transition-all ${
                      form.role === r ? 'border-[#F56F05] bg-orange-50' : 'border-zinc-100 hover:border-zinc-200'
                    }`} data-testid={`role-${r}`}>
                    <IconComp className={`h-6 w-6 mx-auto mb-2 ${form.role === r ? 'text-[#F56F05]' : 'text-zinc-400'}`} />
                    <p className={`text-sm font-bold ${form.role === r ? 'text-[#F56F05]' : 'text-zinc-600'}`}>{cfg.label}</p>
                    <p className="text-[10px] text-zinc-400 mt-0.5">
                      {r === 'client' ? 'Portal access' : r === 'cleaner' ? 'Cleaner portal' : 'Full access'}
                    </p>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* User Info */}
        <Card className="border-zinc-100">
          <CardContent className="p-4 space-y-4">
            <p className="text-sm font-semibold text-zinc-700">Account Details</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs text-zinc-500">Full Name *</Label>
                <Input value={form.full_name} onChange={e => set('full_name', e.target.value)}
                  className="mt-1 rounded-xl border-zinc-200" placeholder="John Smith" data-testid="create-name" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Email *</Label>
                <Input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                  className="mt-1 rounded-xl border-zinc-200" placeholder="john@example.com" data-testid="create-email" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Phone</Label>
                <Input value={form.phone} onChange={e => set('phone', e.target.value)}
                  className="mt-1 rounded-xl border-zinc-200" placeholder="(971) 555-1234" data-testid="create-phone" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Login Credentials */}
        <Card className="border-zinc-100">
          <CardContent className="p-4 space-y-3">
            <p className="text-sm font-semibold text-zinc-700">Login Credentials</p>

            {/* Invite vs manual-password toggle */}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setSendInvite(true)}
                className={`p-3 rounded-xl border-2 text-left transition-all ${sendInvite ? 'border-[#F56F05] bg-orange-50' : 'border-zinc-100 hover:border-zinc-200'}`}
                data-testid="cred-mode-invite"
              >
                <p className={`text-sm font-bold ${sendInvite ? 'text-[#F56F05]' : 'text-zinc-600'}`}>Send invite email</p>
                <p className="text-[11px] text-zinc-400 mt-0.5">They set their own password</p>
              </button>
              <button
                type="button"
                onClick={() => setSendInvite(false)}
                className={`p-3 rounded-xl border-2 text-left transition-all ${!sendInvite ? 'border-[#F56F05] bg-orange-50' : 'border-zinc-100 hover:border-zinc-200'}`}
                data-testid="cred-mode-manual"
              >
                <p className={`text-sm font-bold ${!sendInvite ? 'text-[#F56F05]' : 'text-zinc-600'}`}>Set password now</p>
                <p className="text-[11px] text-zinc-400 mt-0.5">You&apos;ll communicate it manually</p>
              </button>
            </div>

            {!sendInvite && (
              <div>
                <Label className="text-xs text-zinc-500">Password * (min 6 characters)</Label>
                <div className="relative mt-1">
                  <Input type={showPassword ? 'text' : 'password'} value={form.password}
                    onChange={e => set('password', e.target.value)}
                    className="rounded-xl border-zinc-200 pr-10" placeholder="Temporary password" data-testid="create-password" />
                  <button onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600" type="button">
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {form.password && form.password.length < 6 && (
                  <p className="text-xs text-red-500 mt-1">Password must be at least 6 characters</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary */}
        {canSubmit && (
          <Card className="border-[#F56F05]/20 bg-orange-50/50">
            <CardContent className="p-4">
              <p className="text-sm text-zinc-700">
                Creating <strong>{form.full_name}</strong> as <Badge className={`${ROLE_CONFIG[form.role].color} border text-[10px]`}>{ROLE_CONFIG[form.role].label}</Badge> with email <strong>{form.email}</strong>
              </p>
              <p className="text-xs text-zinc-500 mt-1">
                {sendInvite
                  ? `We'll email them a welcome link to set their password (expires in 7 days).`
                  : `They can sign in at gleamgurus.com with these credentials.`}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Submit */}
        <div className="flex justify-end gap-3 pb-8">
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="cancel-create">Cancel</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || submitting}
            className="bg-[#F56F05] hover:bg-[#e06505] text-white rounded-xl px-8" data-testid="submit-user-bottom">
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            Create Account
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminUsersPage;
